import { zonedTimeToUtc } from 'date-fns-tz';
import {
  add as addUnit,
  differenceInDays,
  formatISO,
  isAfter,
  isBefore,
  parseISO,
  Duration,
} from 'date-fns';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  getDate,
  getMonth,
  getYear,
  setDate as setDateFns,
  setMonth,
  setYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns/fp';
import { identity } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { match, P } from 'ts-pattern';
import { getTimeZone } from './timezone';
import { DateInput, DateParts } from './types';

export { setDate as setDay, setMonth, setYear } from 'date-fns/fp';

/**
 * parseDate
 *
 * Parse a date input and ensure it is cast to UTC. String input must be ISO format. Passing in a TZ (or letting it
 * "guess" the browser's TZ) will ***override*** whatever TZ the passed `date` has with it.
 *
 * @param input Optional date input. If none provided, date will be "now"
 * @param tz Optional timezone to use for converting to UTC. If none provided, tz will be guessed.
 * @return Date in UTC
 *
 * @example "2023-02-21T10:52:12.048", "America/Los_Angeles" => Date set to 2023-02-21 18:52:12.048 UTC
 * @example Date set to 2023-02-21 10:52:12.048 UTC, false => Date set to 2023-02-21 10:52:12.048 UTC
 * @example 1676983401000 (TZ is guessed to be America/Chicago) => Date set to 2023-02-21T12:43:21.000 UTC
 */
export const parseDate = (input?: DateInput, tz?: string | true): Date => {
  const timezone = getTimeZone(tz);

  const date = match(input)
    .with(P.string, (stringValue) => parseISO(stringValue))
    .with(P.number, (numberValue) => new Date(numberValue))
    .with(undefined, () => new Date())
    .with(P.instanceOf(Date), identity.of)
    .exhaustive();

  return match(timezone)
    .with(undefined, () => date)
    .with(P.string, (tzValue) => zonedTimeToUtc(date, tzValue))
    .exhaustive();
};

export function convertToString(input: Date): string {
  return formatISO(input);
}

type DateArrayType = [
  number,
  number,
  number | undefined,
  number | undefined,
  number | undefined,
  number | undefined,
  number | undefined,
];

const fillDateArray = (value: number[]): DateArrayType => {
  const response: DateArrayType = [new Date().getFullYear(), 1, 1, 0, 0, 0, 0];

  value.forEach((v, i) => {
    if (i >= 7) {
      return;
    }

    response[i] = v;
  });

  return response;
};

export function createDateByArray(value: number[]): Date {
  const [year, month, day, hour, minute, second, millisecond] = fillDateArray(value);

  // Subtract month by 1 because it is based on a 0-index count rather than 1-index
  return new Date(year, month - 1, day, hour, minute, second, millisecond);
}

/**
 * Add the specified years, months, weeks, days, hours, minutes and seconds to the given date.
 *
 * @example
 * addDurationToDate(new Date(2014, 8, 1, 10, 19, 50), { years: 2, months: 9, weeks: 1, days: 7, hours: 5, minutes: 9, seconds: 30 }) will turn 1 September 2014, 10:19:50 --> Thu Jun 15 2017 15:29:20
 */
export const addDurationToDate = (input: DateInput, duration: Duration) =>
  addUnit(parseDate(input), duration);

/**
 * Updates the first datetime value with the year/month/date of the second.
 */
export function setDate(currentDate?: DateInput, newDate?: Date): Date {
  if (!newDate) {
    return parseDate(currentDate);
  }

  return pipe(
    parseDate(currentDate),
    setYear(getYear(newDate)),
    setMonth(getMonth(newDate)),
    setDateFns(getDate(newDate)),
  );
}

function getModifyMethod(unit: DateParts, add = true) {
  return match(unit)
    .with(DateParts.YEAR, () => (add ? addYears : subYears))
    .with(DateParts.MONTH, () => (add ? addMonths : subMonths))
    .with(DateParts.WEEK, () => (add ? addWeeks : subWeeks))
    .with(DateParts.DATE, () => (add ? addDays : subDays))
    .exhaustive();
}

export function modifyDateUnit(
  date?: DateInput,
  add = true,
  amount = 1,
  unit = DateParts.DATE,
): Date {
  return pipe(parseDate(date), getModifyMethod(unit, add)(amount));
}

export function isDateBefore(a?: DateInput, b: Date = parseDate()): boolean {
  return pipe(parseDate(a), (v) => isBefore(v, b));
}

export function isDateAfter(a?: DateInput, b: Date = parseDate()): boolean {
  return pipe(parseDate(a), (v) => isAfter(v, b));
}

export function isDateBetween(a?: DateInput, start?: Date, end?: Date): boolean {
  return isDateBefore(a, end) && isDateAfter(a, start);
}

export function getDaysAgo(a?: DateInput, amount = 1): Date {
  return modifyDateUnit(a, false, amount, DateParts.DATE);
}

export function getDaysTo(a?: DateInput, amount = 1): Date {
  return modifyDateUnit(a, true, amount, DateParts.DATE);
}

export function getWeeksAgo(a?: DateInput, amount = 1): Date {
  return modifyDateUnit(a, false, amount, DateParts.WEEK);
}

export function getWeeksTo(a?: DateInput, amount = 1): Date {
  return modifyDateUnit(a, true, amount, DateParts.WEEK);
}

export function getMonthsAgo(a?: DateInput, amount = 1): Date {
  return modifyDateUnit(a, false, amount, DateParts.MONTH);
}

export function getMonthsTo(a?: DateInput, amount = 1): Date {
  return modifyDateUnit(a, true, amount, DateParts.MONTH);
}

export function recencyInDays(a?: DateInput): number {
  return differenceInDays(parseDate(a), parseDate());
}
